// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useSiteSettings } from "../hooks/useSiteSettings";

export default function SEOHead({ title, description, keywords, page }) {
  const [location] = useLocation();
  // Fallback canonical URL if not provided by API
  const fallbackCanonicalUrl = `https://codeteki.au${location === "/" ? "" : location.replace(/\/$/, "")}`;

  const { settings } = useSiteSettings();

  const { data: pageSeoData } = useQuery({
    queryKey: ["/api/seo/", page || "home"],
    queryFn: async () => {
      const params = page ? `?page=${encodeURIComponent(page)}` : "";
      const response = await fetch(`/api/seo/${params}`);
      if (!response.ok) throw new Error("Failed to fetch page SEO");
      return response.json();
    },
    staleTime: 1000 * 60 * 5,
  });

  const pageSEO = pageSeoData?.data?.seo || pageSeoData?.seo || null;

  let structuredData = settings?.structuredData || settings?.structured_data || null;
  if (structuredData && typeof structuredData === "string") {
    try {
      structuredData = JSON.parse(structuredData);
    } catch {
      structuredData = null;
    }
  }
  if (!structuredData) {
    structuredData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": settings?.siteName || "Codeteki",
      "description":
        settings?.siteDescription ||
        "AI-powered business solutions including chatbots, voice assistants, and custom automation. Melbourne-based AI development team.",
      "url": "https://codeteki.au",
      "telephone": settings?.contact?.primaryPhone || "+61 469 754 386",
      "email": settings?.contact?.primaryEmail || "info@codeteki.au",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": settings?.contact?.address || "Melbourne",
        "addressRegion": "Victoria",
        "addressCountry": "AU",
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-37.8136",
        "longitude": "144.9631",
      },
      "openingHours": ["Mo-Fr 09:00-17:00"],
      "areaServed": {
        "@type": "Country",
        "name": "Australia",
      },
      "makesOffer": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Workforce Solutions",
            "description": "Intelligent AI chatbots and voice agents for customer service automation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Web Development",
            "description": "Custom-built, mobile-optimized websites with AI integration"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Business Automation",
            "description": "Custom automation solutions and workflow integrations"
          }
        }
      ],
    };
  }

  const cleanTitle =
    (typeof pageSEO?.metaTitle === "string" && pageSEO.metaTitle) ||
    title ||
    settings?.siteName ||
    "Codeteki - AI Business Solutions";

  const cleanDescription =
    (typeof pageSEO?.metaDescription === "string" && pageSEO.metaDescription) ||
    description ||
    settings?.siteTagline ||
    "AI-Powered Business Solutions for Modern Entrepreneurs";

  const cleanKeywords =
    (typeof pageSEO?.metaKeywords === "string" && pageSEO.metaKeywords) ||
    keywords ||
    "AI chatbot development Australia, voice assistant Melbourne, business automation, website development Melbourne";

  const ogImage = pageSEO?.ogImage || settings?.logos?.main || "https://www.codeteki.au/favicon.png";

  // Use canonical URL from API, fallback to generated one
  const canonicalUrl = pageSEO?.canonicalUrl || fallbackCanonicalUrl;

  const truncatedTitle = cleanTitle.length > 60 ? cleanTitle.substring(0, 57) + "..." : cleanTitle;

  const finalTitle = truncatedTitle;
  const finalDescription = cleanDescription;
  const finalKeywords = cleanKeywords;

  // OG tags - use specific OG fields from API if available, otherwise fallback to meta
  const finalOgTitle = pageSEO?.ogTitle || finalTitle;
  const finalOgDescription = pageSEO?.ogDescription || finalDescription;

  return (
    <Helmet>
      {/* Title is the only tag Helmet replaces (not appends) — safe to keep */}
      <title>{finalTitle}</title>

      {/* Structured data may differ from Django's page-level schema */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Resource hints not in Django template */}
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://api.openai.com" />
    </Helmet>
  );
}
